export interface FileOverwriteOptions {

    /**
     * Set to `true` to overwrite a file if it exists. Will
     * throw an error otherwise if the file does exist.
     */
    readonly overwrite: boolean;
}

export interface FileUnlockOptions {

    /**
     * Set to `true` to try to remove any write locks the file might
     * have. A file that is write locked will throw an error for any
     * attempt to write to unless `unlock: true` is provided.
     */
    readonly unlock: boolean;
}

export interface FileAtomicReadOptions {

    /**
     * The optional `atomic` flag can be used to make sure
     * the `readFile` method is not running in parallel with
     * any `write` operations in the same process.
     *
     * Typically you should not need to use this flag but if
     * for example you are quickly reading a file right after
     * a file event occurred and the file changes a lot, there
     * is a chance that a read returns an empty or partial file
     * because a pending write has not finished yet.
     *
     * Note: this does not prevent the file from being written
     * to from a different process. If you need such atomic
     * operations, you better use a real database as storage.
     */
    readonly atomic: true;
}

export interface FileReadStreamOptions {

    /**
     * Is an integer specifying where to begin reading from in the file. If position is undefined,
     * data will be read from the current file position.
     */
    readonly position?: number;

    /**
     * Is an integer specifying how many bytes to read from the file. By default, all bytes
     * will be read.
     */
    readonly length?: number;

    /**
     * If provided, the size of the file will be checked against the limits.
     */
    limits?: {
        readonly size?: number;
        readonly memory?: number;
    };
}

export interface FileWriteOptions extends FileOverwriteOptions, FileUnlockOptions {

    /**
     * Set to `true` to create a file when it does not exist. Will
     * throw an error otherwise if the file does not exist.
     */
    readonly create: boolean;
}

export type FileOpenOptions = FileOpenForReadOptions | FileOpenForWriteOptions;

export function isFileOpenForWriteOptions(options: FileOpenOptions): options is FileOpenForWriteOptions {
    return options.create === true;
}

export interface FileOpenForReadOptions {

    /**
     * A hint that the file should be opened for reading only.
     */
    readonly create: false;
}

export interface FileOpenForWriteOptions extends FileUnlockOptions {

    /**
     * A hint that the file should be opened for reading and writing.
     */
    readonly create: true;
}

export interface FileDeleteOptions {

    /**
     * Set to `true` to recursively delete any children of the file. This
     * only applies to folders and can lead to an error unless provided
     * if the folder is not empty.
     */
    readonly recursive: boolean;

    /**
     * Set to `true` to attempt to move the file to trash
     * instead of deleting it permanently from disk. This
     * option maybe not be supported on all providers.
     */
    readonly useTrash: boolean;
}

export enum FileType {

    /**
     * File is unknown (neither file, directory nor symbolic link).
     */
    Unknown = 0,

    /**
     * File is a normal file.
     */
    File = 1,

    /**
     * File is a directory.
     */
    Directory = 2,

    /**
     * File is a symbolic link.
     *
     * Note: even when the file is a symbolic link, you can test for
     * `FileType.File` and `FileType.Directory` to know the type of
     * the target the link points to.
     */
    SymbolicLink = 64
}

export enum FilePermission {

    /**
     * File is readonly.
     */
    Readonly = 1
}

export interface IStat {

    /**
     * The file type.
     */
    readonly type: FileType;

    /**
     * The last modification date represented as millis from unix epoch.
     */
    readonly mtime: number;

    /**
     * The creation date represented as millis from unix epoch.
     */
    readonly ctime: number;

    /**
     * The size of the file in bytes.
     */
    readonly size: number;

    /**
     * The file permissions.
     */
    readonly permissions?: FilePermission;
}

export interface IWatchOptions {

    /**
     * Set to `true` to watch for changes recursively in a folder
     * and all of its children.
     */
    readonly recursive: boolean;

    /**
     * A set of paths to exclude from watching.
     */
    excludes: string[];
}

export const enum FileSystemProviderCapabilities {

    /**
     * Provider supports unbuffered read/write.
     */
    FileReadWrite = 1 << 1,

    /**
     * Provider supports open/read/write/close low level file operations.
     */
    FileOpenReadWriteClose = 1 << 2,

    /**
     * Provider supports stream based reading.
     */
    FileReadStream = 1 << 4,

    /**
     * Provider supports copy operation.
     */
    FileFolderCopy = 1 << 3,

    /**
     * Provider is path case sensitive.
     */
    PathCaseSensitive = 1 << 10,

    /**
     * All files of the provider are readonly.
     */
    Readonly = 1 << 11,

    /**
     * Provider supports to delete via trash.
     */
    Trash = 1 << 12,

    /**
     * Provider support to unlock files for writing.
     */
    FileWriteUnlock = 1 << 13,

    /**
     * Provider support to read files atomically. This implies the
     * provider provides the `FileReadWrite` capability too.
     */
    FileAtomicRead = 1 << 14
}

export const enum FileOperation {
    CREATE,
    DELETE,
    MOVE,
    COPY,
    WRITE
}

/**
 * Possible changes that can occur to a file.
 */
export const enum FileChangeType {
    UPDATED,
    ADDED,
    DELETED
}

/**
 * Identifies a single change in a file.
 */
export interface IFileChange {

    /**
     * The type of change that occurred to the file.
     */
    readonly type: FileChangeType;

    /**
     * The unified resource identifier of the file that changed.
     */
    readonly resource: string;
}

interface IBaseStat {

    /**
     * The unified resource identifier of this file or folder.
     */
    readonly resource: {
        path: string;
        scheme: string;
    };

    /**
     * The name which is the last segment
     * of the {{path}}.
     */
    readonly name: string;

    /**
     * The size of the file.
     *
     * The value may or may not be resolved as
     * it is optional.
     */
    readonly size?: number;

    /**
     * The last modification date represented as millis from unix epoch.
     *
     * The value may or may not be resolved as
     * it is optional.
     */
    readonly mtime?: number;

    /**
     * The creation date represented as millis from unix epoch.
     *
     * The value may or may not be resolved as
     * it is optional.
     */
    readonly ctime?: number;

    /**
     * A unique identifier thet represents the
     * current state of the file or directory.
     *
     * The value may or may not be resolved as
     * it is optional.
     */
    readonly etag?: string;

    /**
     * The file is read-only.
     */
    readonly readonly?: boolean;
}

export interface IBaseStatWithMetadata extends Required<IBaseStat> { }

/**
 * A file resource with meta information.
 */
export interface IFileStat extends IBaseStat {

    /**
     * The resource is a file.
     */
    readonly isFile: boolean;

    /**
     * The resource is a directory.
     */
    readonly isDirectory: boolean;

    /**
     * The resource is a symbolic link. Note: even when the
     * file is a symbolic link, you can test for `FileType.File`
     * and `FileType.Directory` to know the type of the target
     * the link points to.
     */
    readonly isSymbolicLink: boolean;

    /**
     * The children of the file stat or undefined if none.
     */
    children?: IFileStat[];
}

export interface IFileStatWithMetadata extends IFileStat, IBaseStatWithMetadata {
    readonly mtime: number;
    readonly ctime: number;
    readonly etag: string;
    readonly size: number;
    readonly readonly: boolean;
    readonly children?: IFileStatWithMetadata[];
}

export interface IResolveFileResult {
    readonly stat?: IFileStat;
    readonly success: boolean;
}

export interface IResolveFileResultWithMetadata extends IResolveFileResult {
    readonly stat?: IFileStatWithMetadata;
}

export interface IFileContent extends IBaseStatWithMetadata {

    /**
     * The content of a file as buffer.
     */
    readonly value: string;
}

export interface IBaseReadFileOptions extends FileReadStreamOptions {

    /**
     * The optional etag parameter allows to return early from resolving the resource if
     * the contents on disk match the etag. This prevents accumulated reading of resources
     * that have been read already with the same etag.
     * It is the task of the caller to makes sure to handle this error case from the promise.
     */
    readonly etag?: string;
}

export interface IReadFileStreamOptions extends IBaseReadFileOptions { }

export interface IReadFileOptions extends IBaseReadFileOptions {

    /**
     * The optional `atomic` flag can be used to make sure
     * the `readFile` method is not running in parallel with
     * any `write` operations in the same process.
     *
     * Typically you should not need to use this flag but if
     * for example you are quickly reading a file right after
     * a file event occurred and the file changes a lot, there
     * is a chance that a read returns an empty or partial file
     * because a pending write has not finished yet.
     *
     * Note: this does not prevent the file from being written
     * to from a different process. If you need such atomic
     * operations, you better use a real database as storage.
     */
    readonly atomic?: boolean;
}

export interface IWriteFileOptions {

    /**
     * The last known modification time of the file. This can be used to prevent dirty writes.
     */
    readonly mtime?: number;

    /**
     * The etag of the file. This can be used to prevent dirty writes.
     */
    readonly etag?: string;

    /**
     * Whether to attempt to unlock a file before writing.
     */
    readonly unlock?: boolean;
}

export interface IResolveFileOptions {

    /**
     * Automatically continue resolving children of a directory until the provided resources
     * are found.
     */
    readonly resolveTo?: readonly string[];

    /**
     * Automatically continue resolving children of a directory if the number of children is 1.
     */
    readonly resolveSingleChildDescendants?: boolean;

    /**
     * Will resolve mtime, ctime, size and etag of files if enabled. This can have a negative impact
     * on performance and thus should only be used when these values are required.
     */
    readonly resolveMetadata?: boolean;
}

export interface IResolveMetadataFileOptions extends IResolveFileOptions {
    readonly resolveMetadata: true;
}

export interface ICreateFileOptions {

    /**
     * Overwrite the file to create if it already exists on disk. Otherwise
     * an error will be thrown (FILE_MODIFIED_SINCE).
     */
    readonly overwrite?: boolean;
}
